<template>
    <OrderSummaryCard data-component="OrderTicketSummaryCard"
                      :title="l('ORDER_DETAILS.ORDER_SUMMARY')">
        <template v-slot:totals>
            {{ l('ORDER_DETAILS.SUMMARY_TICKET_COUNT', { count: totalQty }) }}
            &middot;
            {{ l('ORDER_DETAILS.SUMMARY_REDEEMED_COUNT', { count: totalRedeemedQty }) }}
        </template>

        <template v-slot:rows>
            <div v-for="group in tab.groups"
                 :key="group.ID"
                 data-testid="summaryRow"
                 class="order-summary-card__row order-summary-card__row--clickable"
                 role="button"
                 tabindex="0"
                 @click="$emit('select-group', group)"
                 @keyup.enter="$emit('select-group', group)">
                <div class="order-summary-card__row-main">
                    <span data-testid="summaryRowName">
                        {{ group.session ? group.session.name : l(group.getNameLangKey()) }}
                    </span>
                    <span v-if="group.session"
                          class="order-summary-card__row-time"
                          data-testid="summaryRowSessionTime">
                        {{ timeFilter(group.session.startTime, true) }}
                    </span>
                    <TodayBadge v-if="group.session"
                                data-testid="summaryRowTodayBadge"
                                :date="group.session.startTime" />
                    <Badge v-if="group.isValidOpenTicket">
                        {{ l('VALID_ANY_DAY') }}
                    </Badge>
                    <BookingGroupNameBadge v-if="group.bookingGroup"
                                           data-testid="summaryRowBookingGroup"
                                           :name="group.bookingGroup.name" />
                </div>

                <div class="order-summary-card__row-meta">
                    <span data-testid="summaryRowRedemptions"
                          class="order-summary-card__row-redemptions"
                          :class="{'order-summary-card__row-redemptions--redeemed': groupRedeemedQty(group) > 0}">
                        {{ l('ORDER_DETAILS.SUMMARY_GROUP_REDEEMED', {
                            redeemed: groupRedeemedQty(group),
                            total: groupQty(group),
                        }) }}
                    </span>
                </div>
            </div>
        </template>

        <template v-if="showRedeemOrderFooter"
                  v-slot:footer>
            <RedeemOrderButton :groups="tab.groups"
                               :is-order-live="currentOrder.isLive()" />
        </template>
    </OrderSummaryCard>
</template>

<script>
import Badge from '@/vue/components/Badges/Badge';
import BookingGroupNameBadge from '@/epos/vue/pages/order-details/components/BookingGroupNameBadge';
import OrderSummaryCard from '@/epos/vue/pages/order-details/components/OrderSummaryCard';
import RedeemOrderButton from '@/epos/vue/pages/order-details/components/RedeemOrderButton';
import TodayBadge from '@/epos/vue/components/Badges/TodayBadge';

export default {
    name: 'OrderTicketSummaryCard',

    components: { Badge, BookingGroupNameBadge, OrderSummaryCard, RedeemOrderButton, TodayBadge },

    props: {
        // The current (Tickets) tab, i.e. { itemType, nameLangKey, groups }.
        tab: {
            type: Object,
            required: true,
        },
        currentOrder: {
            type: Object,
            required: true,
        },
    },

    emits: ['select-group'],

    computed: {
        showRedeemOrderButton() {
            return this.$store.state.currentDevice.showRedeemOrderButton;
        },

        /**
         * @return {boolean}
         */
        showRedeemOrderFooter() {
            return this.showRedeemOrderButton && this.currentOrder.isLive();
        },

        /**
         * Total qty of tickets on this tab.
         *
         * @return {number}
         */
        totalQty() {
            return this.tab.groups.reduce((sum, group) => sum + this.groupQty(group), 0);
        },

        /**
         * How many of the tickets on this tab have been redeemed.
         *
         * @return {number}
         */
        totalRedeemedQty() {
            return this.tab.groups.reduce((sum, group) => sum + this.groupRedeemedQty(group), 0);
        },
    },

    methods: {
        /**
         * Total qty of items in a group.
         *
         * Bundles result in cloned lines. We need to exclude these or the qty is wrong because of double counting.
         *
         * @param {OrderItemGroup} group
         *
         * @return {number}
         */
        groupQty(group) {
            return group.items
                .filter((item) => !item.isClone)
                .reduce((sum, item) => sum + item.qty, 0);
        },

        /**
         * How many of the items in a group have been redeemed.
         *
         * @param {OrderItemGroup} group
         *
         * @return {number}
         */
        groupRedeemedQty(group) {
            return group.items
                .filter((item) => !item.isClone)
                .reduce((sum, item) => sum + this.orderLineRedeemedQty(item), 0);
        },

        /**
         * How many of a line's tickets have been redeemed.
         *
         * Lines printed per instance track redemptions on the instances rather than the line itself.
         *
         * @param {OrderLine} orderLine
         *
         * @return {number}
         */
        orderLineRedeemedQty(orderLine) {
            if (orderLine.hasIndividualTicketInstances()) {
                return orderLine.summaryItemInstanceNetRedemptions();
            }

            return orderLine.redeemed;
        },
    },
};
</script>
